import { Component, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { ToastrService } from 'ngx-toastr';

import { SiteTypeTableService } from 'src/service/site-type.service';

import { CustomerService, DeviceService, SiteTypeDeviceActivityPlanService } from '../../../api/services';

import { ApplicationConfigService } from 'src/service/application-config.service';
import { AzureAuthService } from 'src/service/azure-auth.service';

import { SiteType } from 'src/api/models/site-type';
import { IconService } from 'src/service/icon.service';
import { DeviceActivityRecord, DeviceActivityType, DeviceManufacturer, SiteTypeDeviceActivityPlan, SiteTypeDeviceActivityPlanConfig } from 'src/api/models';


@Component({
  selector: 'app-site-types',
  templateUrl: './site-types.component.html',
  styleUrls: ['./site-types.component.css']
})
export class SiteTypesComponent implements OnInit {

  public dataType: String = "Site Type";
  public newItem: SiteType = {} as SiteType;
  public siteType: SiteType;
  public activityTypes: DeviceActivityType[] = [];
  public activityPlans: SiteTypeDeviceActivityPlan[] = [];
  public manufacturers: DeviceManufacturer[] = [];
  public deviceActivities: DeviceActivityRecord[] = [];
  public showMetadataForActivityId: number | null = null;
  public metadataAnimationState: 'hidden' | 'showing' | 'hiding' = 'hidden';

  public remoteData: Observable<any[]>;

  public showPinMenu: boolean = false;

  constructor(
    public service: SiteTypeTableService,
    private app: ApplicationConfigService,
    private icons: IconService,
    private customerService: CustomerService,
    private deviceSerivce: DeviceService,
    private activityService: SiteTypeDeviceActivityPlanService,
    public azureAuthService: AzureAuthService,
    private toastr: ToastrService) {
    this.remoteData = this.service.remoteData;
    app.customerChanged$.subscribe(cid => {
      this.ngOnInit();
      this.icons.getPins(null);
    });
    this.icons.getPins(null);
  }

  ngOnInit(): void {
    if (this.azureAuthService.isGlobalViewer()) {
      if (this.app.customers == null) {
        this.customerService.CustomerGetAllCustomers().subscribe(c => {
          this.app.customers = c;
        });
      }
      this.service.get(this.app.customerId);
    } else {
      this.app.customerId = this.azureAuthService.userProfile.customerId;
      this.service.get(this.app.customerId);
    }
    this.activityService.SiteTypeDeviceActivityPlanGetAllActivityTypes().subscribe(a => {
      this.activityTypes = a;
    });
    this.deviceSerivce.DeviceGetDeviceManufacturers().subscribe(m => {
      this.manufacturers = m;
    });
  }

  public newSiteType: SiteType;
  public addSiteType() {
    this.newSiteType = {} as SiteType;
  }

  public changeCustomer() {
    this.service.get(this.app.customerId);
  }

  public removeSitetype(siteType) {
    this.service.remove(siteType);
  }

  public updateSiteType() {
    this.service.update(this.siteType);
  }

  public saveNewItem() {
    this.newSiteType.customerId = this.app.customerId;
    // post to service then add to table
    this.service.add(this.newSiteType);
  }

  public showFieldDeploy(siteType: SiteType) {
    this.siteType = siteType;
    this.activityPlans = null;
    //load field deploy data
    this.activityService.SiteTypeDeviceActivityPlanGetAllForSiteType({ customerId: this.app.customerId, siteTypeId: this.siteType.siteTypeId }).subscribe(ad => {
      //map to activity types
      // convert to array
      for (let at of ad) {
        for (let c of at.activityConfig) {
          if (c.dataType == 4 && c.selectOptions == null) { c.selectOptions = []; }
        }
      }
      this.activityPlans = ad;
    });
    this.activityService.SiteTypeDeviceActivityPlanGetDeviceActivities(this.app.customerId).subscribe(dt => {
      this.deviceActivities = dt;
    });
  }

  public deleteActivity(plan: DeviceActivityRecord) {
    if (confirm("Are you sure you want to delete this activity?")) {
      this.activityService.SiteTypeDeviceActivityPlanDeleteDeviceActivity({ customerId: this.app.customerId, deviceActivityId: plan.id }).subscribe(() => {
        const index = this.deviceActivities.indexOf(plan);
        this.deviceActivities.splice(index, 1);
        this.deviceActivities = this.deviceActivities.slice();
      });
    }
  }

  public reprocessActivity(plan: DeviceActivityRecord) {
    this.activityService.SiteTypeDeviceActivityPlanReprocessDeviceActivity({ customerId: this.app.customerId, deviceActivityId: plan.id }).subscribe(() => {
      plan.processedDateTime = null;
    });
  }

  public toggleMetadata(activityId: number) {
    if (this.showMetadataForActivityId === activityId) {
      this.showMetadataForActivityId = null;
    } else {
      this.showMetadataForActivityId = activityId;
    }
  }

  public closeMetadata() {
    this.showMetadataForActivityId = null;
  }

  public getActivityById(activityId: number): DeviceActivityRecord | undefined {
    return this.deviceActivities.find(da => da.id === activityId);
  }

  public updateActivityMetadata(updateData: { metadata: any, identifier: string }) {
    if (this.showMetadataForActivityId === null) return;

    const activity = this.getActivityById(this.showMetadataForActivityId);
    if (!activity) {
      this.toastr.error('Activity not found');
      return;
    }

    // Update both the metadata and device ID
    activity.activityMetaData = updateData.metadata;
    activity.deviceId = updateData.identifier;
    activity.processedLogs = null; 
    console.log('Updated activity metadata and device ID locally', activity);

    // Make single API call to update both fields
     this.activityService.SiteTypeDeviceActivityPlanUpdateDeviceActivity({
       customerId: this.app.customerId, 
       deviceActivityId: activity.id,
       deviceActivity: activity
     }).subscribe(
       () => {
         this.toastr.success('Activity updated successfully');
       },
       error => {
         this.toastr.error('Failed to update activity: ' + error.message);
         // Revert the local change on error
         this.activityService.SiteTypeDeviceActivityPlanGetDeviceActivities(this.app.customerId).subscribe(dt => {
           this.deviceActivities = dt;
         });
       }
     );

    // Refresh the displayed data
    const index = this.deviceActivities.findIndex(da => da.id === activity.id);
    if (index >= 0) {
      this.deviceActivities[index] = { ...activity };
      this.deviceActivities = this.deviceActivities.slice();
    }
  }

  public getActivityPlanForType(at: DeviceActivityType): SiteTypeDeviceActivityPlan {
    if (this.activityPlans == null) return null;
    const ap = this.activityPlans.find(ap => ap.activityType.id == at.id);
    return ap;
  }

  addStep(plan: SiteTypeDeviceActivityPlan) {
    //if (this.activityPlans == null) this.activityPlans = [];
    let ap = { name: null, description: null, dataType: null, example: null, selectOptions: [] } as SiteTypeDeviceActivityPlanConfig;
    plan.activityConfig.push(ap);
    console.log(plan);
    plan.activityConfig = plan.activityConfig.slice();
  }

  removeStep(plan: SiteTypeDeviceActivityPlan, index: number) {
    plan.activityConfig.splice(index, 1);
    plan.activityConfig = plan.activityConfig.slice();
  }

  saveActivityPlan(siteType: SiteType, plan: SiteTypeDeviceActivityPlan) {
    if (plan.id == null || plan.id == 0) {
      this.activityService.SiteTypeDeviceActivityPlanInsert({ customerId: this.app.customerId, siteTypeId: siteType.siteTypeId, newplan: plan }).subscribe(sp => {
        //replace temp plan with saved plan
        const index = this.activityPlans.indexOf(plan);
        this.activityPlans[index] = sp;
        this.activityPlans = this.activityPlans.slice();
      });
    } else {
      console.log(plan);
      this.activityService.SiteTypeDeviceActivityPlanUpdate({ customerId: this.app.customerId, updatePlan: plan }).subscribe(sp => {
        //replace temp plan with saved plan
        const index = this.activityPlans.indexOf(plan);
        this.activityPlans[index] = sp;
        this.activityPlans = this.activityPlans.slice();
      });
    }
  }

  public updateFieldDeploy() {
    //todo
  }

  //update index of activities based on drag order
  onOrderChange(sortedActivities: SiteTypeDeviceActivityPlanConfig[], planToUpdate: SiteTypeDeviceActivityPlan): void {
    planToUpdate.activityConfig.forEach((item, newIndex) => {
      item.index = newIndex;
    });
  }
}
